import AntIcon from "@/components/icons/AntIcon";
import { selectTagsStandardProps } from "@/utils/componentUtils";
import { validationConstants, validations } from "@/utils/validationUtils";
import { Button, Checkbox, Col, Form, Input, Row, Select } from "antd";
import { CheckboxChangeEvent } from "antd/lib/checkbox";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { rowGutter } from "../../../../../../common/constants";
import { CommissionProcessingResult, PostponementReason, postponementReasonTMap } from "../../../enums";
import { CommissionsFilterPageRequest } from "../../../types";
import PostponementReasonTag from "../../PostponementReasonTag";

const colSpan = 6;

type CommissionsFilterPageRequestForm = CommissionsFilterPageRequest & { onlyPostponed: boolean };

interface Props {
  filter: CommissionsFilterPageRequest;
  onFilterSubmit: (filter: CommissionsFilterPageRequest) => void;
}

const BatchCommissionFilterForm = ({ filter, onFilterSubmit }: Props) => {
  const { t } = useTranslation();

  const [form] = Form.useForm<CommissionsFilterPageRequestForm>();

  useEffect(() => {
    form.setFieldsValue({
      keyword: filter.keyword,
      processingResult: filter.processingResult,
      onlyPostponed: filter.processingResult === CommissionProcessingResult.POSTPONED,
      postponementReasons: filter.postponementReasons || [],
    });
  }, []);

  const handleOnlyPostponedChange = (event: CheckboxChangeEvent) => {
    if (!event.target.checked) {
      form.setFieldsValue({ postponementReasons: [] });
    }
  };

  const handleFilterSubmit = (filter: CommissionsFilterPageRequestForm) => {
    const { onlyPostponed, ...processedFilter }: CommissionsFilterPageRequestForm = {
      ...filter,
      processingResult: filter.onlyPostponed ? CommissionProcessingResult.POSTPONED : undefined,
    };

    onFilterSubmit(processedFilter);
  };

  return (
    <Form form={form} layout="vertical" onFinish={handleFilterSubmit} name="commissionFilterForm">
      <Row gutter={rowGutter} justify="center">
        <Col span={colSpan}>
          <Form.Item
            name="keyword"
            label={t("common.searchKeyword")}
            rules={[
              validations.size(
                validationConstants.SEARCH_KEYWORD_MIN_LENGTH,
                validationConstants.SEARCH_KEYWORD_MAX_LENGTH,
              ),
            ]}
          >
            <Input allowClear placeholder={t("commissions.batch.helpers.commissionsSearchHint")} />
          </Form.Item>
        </Col>

        <Col span={colSpan} className="center-align">
          <Form.Item
            name="onlyPostponed"
            className="form-item-without-label"
            valuePropName="checked"
            rules={[validations.none]}
            initialValue={false}
          >
            <Checkbox onChange={handleOnlyPostponedChange}>{t("commissions.batch.filter.onlyPostponed")}</Checkbox>
          </Form.Item>
        </Col>

        <Col span={colSpan}>
          <Form.Item noStyle shouldUpdate={(prev, next) => prev.onlyPostponed !== next.onlyPostponed}>
            {({ getFieldValue }) => (
              <Form.Item
                name="postponementReasons"
                label={t("commissions.batch.enums.postponementReason._label")}
                rules={[validations.none]}
              >
                <Select
                  {...selectTagsStandardProps(postponementReasonTMap)}
                  allowClear
                  mode="multiple"
                  maxTagCount="responsive"
                  disabled={!getFieldValue("onlyPostponed")}
                  tagRender={(props) => (
                    <PostponementReasonTag
                      reason={props.value}
                      closable={props.closable}
                      onClose={props.onClose}
                      label={props.label}
                    />
                  )}
                  options={Object.values(PostponementReason).map((reason) => ({
                    value: reason,
                    label: <PostponementReasonTag reason={reason} />,
                  }))}
                />
              </Form.Item>
            )}
          </Form.Item>
        </Col>

        <Col flex="110px" className="no-title-space">
          <Form.Item>
            <Button type="primary" htmlType="submit" icon={<AntIcon type="search" />}>
              {t("common.filter")}
            </Button>
          </Form.Item>
        </Col>
      </Row>
    </Form>
  );
};

export default BatchCommissionFilterForm;
